import React, { useEffect, useRef, useState, useCallback } from "react";
import { Mic, MicOff, Video as VideoIcon, VideoOff, MonitorUp, PhoneOff, Loader2 } from "lucide-react";

const ICE = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    // Free community TURN relays for cross-network / cellular reliability (best-effort)
    { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
  ],
};

function Tile({ stream, label, muted, isLocal }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return (
    <div className="relative bg-slate-800 rounded-xl overflow-hidden aspect-video" data-testid={isLocal ? "rtc-local-tile" : "rtc-remote-tile"}>
      <video ref={ref} autoPlay playsInline muted={muted} className="w-full h-full object-cover" />
      <span className="absolute bottom-1.5 left-1.5 text-[11px] font-bold text-white bg-black/50 rounded px-1.5 py-0.5">{label}</span>
    </div>
  );
}

export default function RtcRoom({ roomId, displayName, onLeave }) {
  const [tiles, setTiles] = useState({});      // peerId -> { stream, name }
  const [status, setStatus] = useState("connecting"); // connecting | live | error
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharing, setSharing] = useState(false);

  const wsRef = useRef(null);
  const pcs = useRef({});          // peerId -> RTCPeerConnection
  const names = useRef({});        // peerId -> name
  const localStream = useRef(null);
  const camTrack = useRef(null);   // original camera video track (to restore after screen share)
  const screenTrack = useRef(null);
  const myId = useRef((crypto.randomUUID && crypto.randomUUID()) || `p_${Math.random().toString(36).slice(2)}`);
  const [localStreamState, setLocalStreamState] = useState(null);

  const send = useCallback((obj) => {
    try { wsRef.current?.send(JSON.stringify(obj)); } catch {}
  }, []);

  const sendSignal = useCallback((to, data) => send({ t: "signal", to, data }), [send]);

  const removePeer = useCallback((pid) => {
    try { pcs.current[pid]?.close(); } catch {}
    delete pcs.current[pid];
    delete names.current[pid];
    setTiles((prev) => { const n = { ...prev }; delete n[pid]; return n; });
  }, []);

  const createPeer = useCallback((pid, initiator) => {
    if (pcs.current[pid]) return pcs.current[pid];
    const pc = new RTCPeerConnection(ICE);
    pcs.current[pid] = pc;
    localStream.current?.getTracks().forEach((tr) => pc.addTrack(tr, localStream.current));
    pc.onicecandidate = (e) => { if (e.candidate) sendSignal(pid, { candidate: e.candidate }); };
    pc.ontrack = (e) => {
      setTiles((prev) => ({ ...prev, [pid]: { stream: e.streams[0], name: names.current[pid] || "Guest" } }));
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState)) removePeer(pid);
    };
    if (initiator) {
      pc.createOffer()
        .then((o) => pc.setLocalDescription(o))
        .then(() => sendSignal(pid, { sdp: pc.localDescription }))
        .catch(() => {});
    }
    return pc;
  }, [sendSignal, removePeer]);

  const onSignal = useCallback(async ({ from, data }) => {
    const pc = pcs.current[from] || createPeer(from, false);
    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        if (data.sdp.type === "offer") {
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          sendSignal(from, { sdp: pc.localDescription });
        }
      } else if (data.candidate) {
        await pc.addIceCandidate(data.candidate);
      }
    } catch {}
  }, [createPeer, sendSignal]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1) Get camera + mic (fall back to audio-only, then view-only)
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch {
        try { stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true }); setCamOn(false); } catch { stream = null; setCamOn(false); setMicOn(false); }
      }
      if (cancelled) { stream?.getTracks().forEach((t) => t.stop()); return; }
      localStream.current = stream || new MediaStream();
      camTrack.current = stream?.getVideoTracks()[0] || null;
      setLocalStreamState(localStream.current);

      // 2) Connect signaling WS
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const url = BACKEND.replace(/^http/, "ws") + `/api/rtc/ws/${encodeURIComponent(roomId)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => { send({ t: "join", peer_id: myId.current, name: displayName || "Guest" }); setStatus("live"); };
      ws.onerror = () => setStatus("error");
      ws.onclose = () => { if (!cancelled) setStatus("error"); };
      ws.onmessage = (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        if (m.t === "peers") {
          (m.peers || []).forEach((p) => { names.current[p.peer_id] = p.name; createPeer(p.peer_id, true); });
        } else if (m.t === "peer-joined") {
          names.current[m.peer_id] = m.name; // wait for their offer
        } else if (m.t === "signal") {
          onSignal(m);
        } else if (m.t === "peer-left") {
          removePeer(m.peer_id);
        }
      };
    })();

    return () => {
      cancelled = true;
      try { wsRef.current?.send(JSON.stringify({ t: "leave" })); } catch {}
      try { wsRef.current?.close(); } catch {}
      Object.keys(pcs.current).forEach((pid) => { try { pcs.current[pid].close(); } catch {} });
      pcs.current = {};
      localStream.current?.getTracks().forEach((t) => { try { t.stop(); } catch {} });
      screenTrack.current?.stop?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, displayName]);

  const toggleMic = () => {
    const tr = localStream.current?.getAudioTracks()[0];
    if (tr) { tr.enabled = !tr.enabled; setMicOn(tr.enabled); }
  };
  const toggleCam = () => {
    const tr = localStream.current?.getVideoTracks()[0];
    if (tr) { tr.enabled = !tr.enabled; setCamOn(tr.enabled); }
  };

  const replaceVideoForAll = (track) => {
    Object.values(pcs.current).forEach((pc) => {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
      if (sender && track) sender.replaceTrack(track);
    });
  };

  const stopShare = useCallback(() => {
    screenTrack.current?.stop?.();
    screenTrack.current = null;
    if (camTrack.current) replaceVideoForAll(camTrack.current);
    setSharing(false);
  }, []);

  const startShare = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      alert("Screen sharing isn't supported on this device. (iPhones can't share their screen in a browser — use a computer or Android.)");
      return;
    }
    try {
      const ds = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const st = ds.getVideoTracks()[0];
      screenTrack.current = st;
      replaceVideoForAll(st);
      // show it locally too
      const ls = localStream.current;
      const old = ls.getVideoTracks()[0];
      if (old) ls.removeTrack(old);
      ls.addTrack(st);
      setLocalStreamState(new MediaStream(ls.getTracks()));
      setSharing(true);
      st.onended = () => {
        if (camTrack.current) {
          try { ls.removeTrack(st); ls.addTrack(camTrack.current); } catch {}
          setLocalStreamState(new MediaStream(ls.getTracks()));
        }
        stopShare();
      };
    } catch {}
  };

  const remote = Object.entries(tiles);
  const total = remote.length + 1;
  const gridCols = total <= 1 ? "grid-cols-1" : total <= 2 ? "grid-cols-2" : total <= 4 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className="w-full h-full flex flex-col bg-slate-950" data-testid="rtc-room">
      <div className={`flex-1 grid ${gridCols} gap-2 p-2 overflow-auto content-center`}>
        {localStreamState && <Tile stream={localStreamState} label={`${displayName || "You"} (you)${sharing ? " · sharing" : ""}`} muted isLocal />}
        {remote.map(([pid, t]) => <Tile key={pid} stream={t.stream} label={t.name} muted={false} />)}
        {remote.length === 0 && (
          <div className="col-span-full text-center text-slate-400 text-sm py-6" data-testid="rtc-waiting">
            {status === "error"
              ? "Connection problem — please rejoin."
              : status === "connecting"
              ? <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Starting your camera…</span>
              : "You're in. Waiting for others to join — share the link."}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-2 py-3 bg-slate-900">
        <button onClick={toggleMic} className={`h-11 w-11 rounded-full grid place-items-center ${micOn ? "bg-slate-700 text-white" : "bg-rose-600 text-white"}`} data-testid="rtc-mic" title="Microphone">
          {micOn ? <Mic size={18} /> : <MicOff size={18} />}
        </button>
        <button onClick={toggleCam} className={`h-11 w-11 rounded-full grid place-items-center ${camOn ? "bg-slate-700 text-white" : "bg-rose-600 text-white"}`} data-testid="rtc-cam" title="Camera">
          {camOn ? <VideoIcon size={18} /> : <VideoOff size={18} />}
        </button>
        <button onClick={sharing ? stopShare : startShare} className={`h-11 w-11 rounded-full grid place-items-center ${sharing ? "bg-emerald-600 text-white" : "bg-slate-700 text-white"}`} data-testid="rtc-share" title="Share screen (computer / Android)">
          <MonitorUp size={18} />
        </button>
        <button onClick={onLeave} className="h-11 px-5 rounded-full bg-rose-600 text-white font-black inline-flex items-center gap-1" data-testid="rtc-leave" title="Leave">
          <PhoneOff size={18} /> Leave
        </button>
      </div>
    </div>
  );
}
